import type { Answer } from '../api/generated/Answer'
import type { NewAnswer } from '../api/generated/NewAnswer'
import type { Question } from '../api/generated/Question'
import type { QuestionKind } from '../api/generated/QuestionKind'

/** Answer of a checked `checkbox` question; unchecked is unanswered. */
export const CHECKED = 'true'

/** Answers being edited, by question id. */
export type AnswerForm = Readonly<Record<string, string>>

export const kindLabels: Record<QuestionKind, string> = {
  short_text: 'Texte court',
  long_text: 'Texte long',
  single_choice: 'Choix unique',
  checkbox: 'Case à cocher',
}

export const isQuestionKind = (value: string): value is QuestionKind =>
  Object.hasOwn(kindLabels, value)

export function formOf(answers: readonly Answer[], userId: string): AnswerForm {
  return Object.fromEntries(
    answers.filter((a) => a.user_id === userId).map((a) => [a.question_id, a.value]),
  )
}

/**
 * One answer per question, blank when unanswered: the server replaces all the previous ones.
 * A choice since removed from the options is unanswered.
 */
export function answersOf(form: AnswerForm, questions: readonly Question[]): NewAnswer[] {
  return questions.map((q) => {
    const value = form[q.id] ?? ''
    const valid = q.kind !== 'single_choice' || q.options.includes(value)
    return { question_id: q.id, value: valid ? value : '' }
  })
}

/** The choices typed one per line. */
export function parseOptions(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

/** Whether changing a question's kind deletes its answers, as the server does. */
export function dropsAnswers(from: QuestionKind, to: QuestionKind): boolean {
  const text = (kind: QuestionKind) => kind === 'short_text' || kind === 'long_text'
  return from !== to && !(text(from) && text(to))
}

/** How a volunteer answered, for organizers. */
export function displayAnswer(question: Question, value: string | undefined): string {
  if (question.kind === 'checkbox') return value === CHECKED ? 'Oui' : 'Non'
  return value ?? '—'
}

/** `items` with the one at `from` moved to `to`. */
export function moved<T>(items: readonly T[], from: number, to: number): T[] {
  const result = [...items]
  result.splice(to, 0, ...result.splice(from, 1))
  return result
}
